import { FC, useState, useCallback } from "react";
import { connectRefinementList } from "react-instantsearch-dom";
import { RefinementListProvided } from "react-instantsearch-core";
import { DropdownOutlined } from "src/atoms/DropDown";
import { Trans, t } from "@lingui/macro";
import { InputTextSearch } from "src/molecules/InputTextSearch";
import { useFilterApplied } from "src/organisms/AlgoliaProductList/useFilterApplied";
import { Box } from "@otrium/core";
import { ShowResults } from "../ShowResults";
import { Checkbox } from "src/atoms/Checkbox";
import { HitType } from "src/types/Algolia";
import { useWishListBrands } from "src/hooks/useWishListBrands";
import {
  FilterContentWrapper,
  OutsideClickableWrapper,
  ScrollWrapper,
} from "../DesktopFilter.styled";
import { union, orderBy } from "lodash-es";
import { useLingui } from "@lingui/react";

interface Props extends RefinementListProvided {
  items: HitType[];
  refine: (str: string[]) => void;
  currentIndex: number | null;
  currentRefinement: string[];
  outerRef: React.RefObject<HTMLDivElement>;
  setCurrentIndex: (index: number) => () => void;
  closeFilters: () => void;
  testid?: string;
  isMultiColumn?: boolean;
}

const BrandFilterComponent: FC<Props> = ({
  items,
  refine,
  currentIndex,
  outerRef,
  setCurrentIndex,
  closeFilters,
  isMultiColumn,
}) => {
  const { i18n } = useLingui();
  const [search, setSearch] = useState<string>("");
  const handleClearSearch = useCallback(() => {
    setSearch("");
  }, []);

  const { filterApplied, handleRefine } = useFilterApplied(items, refine);

  const handleChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    (event: any) => setSearch(event.target.value),
    [search, setSearch]
  );

  const { data: favBrandsData } = useWishListBrands(9999, 0);

  if (!items || !items.length) {
    return null;
  }

  const filterBrands = (brand: HitType) => {
    const label = brand.label.split("||");
    const brandName = label[1];

    if (search.length) {
      return (
        brandName.toLowerCase().startsWith(search) ||
        brandName.toLowerCase().includes(search)
      );
    }
    return brand;
  };

  const handleCloseFilter = () => {
    closeFilters();
    setSearch("");
  };

  const sortedBrands = orderBy(items, "count", "desc");
  const selectedBrands = items
    .filter((brand) => {
      return brand.isRefined;
    })
    .map((filteredBrand) => {
      return filteredBrand.label;
    });

  const favBrands = favBrandsData?.wishlistBrands?.items.map((brand) => {
    return brand?.id;
  });
  const favBrandsIds = items
    .filter((brand) => {
      const brandId = brand.label.split("||")[0];
      return favBrands?.includes(brandId);
    })
    .map((mapedBrand) => {
      return mapedBrand.label;
    });
  const toggleFavBrands = () => {
    const isFavBrandsChecked = favBrandsIds.every((selectedBrand) =>
      selectedBrands.includes(selectedBrand)
    );

    if (!isFavBrandsChecked) {
      handleRefine(union(selectedBrands, favBrandsIds))();
    } else {
      const brandIds = selectedBrands.filter((brand) => {
        return !favBrandsIds.includes(brand);
      });
      handleRefine(brandIds)();
    }
  };

  return (
    <DropdownOutlined opened={currentIndex === 0}>
      <DropdownOutlined.Button
        onClick={setCurrentIndex(0)}
        data-testid="filter-brand-button"
      >
        <span>
          <Trans>Brand</Trans>
        </span>
      </DropdownOutlined.Button>
      <DropdownOutlined.Items data-testid="filter-brand-list">
        <OutsideClickableWrapper
          handler={handleCloseFilter}
          outerRef={outerRef}
        >
          <Box mb={3}>
            <InputTextSearch
              placeholder={t(i18n)`Search brand`}
              handleChange={handleChange}
              onClearSearch={handleClearSearch}
            />
          </Box>
          {favBrandsIds.length ? (
            <Box
              bg="tone.sand"
              py={[4, 4, 3]}
              pl={3}
              ml={[0, 0, 0, "-16px"]}
              mb={3}
              width="calc(100% + 32px)"
            >
              <Checkbox
                checked={favBrandsIds.every((selectedBrand) =>
                  selectedBrands.includes(selectedBrand)
                )}
                onChange={toggleFavBrands}
              >
                <span data-testid="favourite-brands-toggle-span">
                  <Trans>Select my favourite brands</Trans>
                </span>
              </Checkbox>
            </Box>
          ) : null}
          <ScrollWrapper>
            <FilterContentWrapper
              isMultiColumn={isMultiColumn && items.length > 1}
              as="ul"
              count={Math.round(items.length / 2)}
            >
              {sortedBrands?.filter(filterBrands).map((item, i) => {
                const label = item.label.split("||");
                const labelName = label[1];
                return (
                  <Box as="li" key={`size-filter-item-${i}`} mb={3}>
                    <Checkbox
                      checked={item.isRefined}
                      onChange={handleRefine(item.value)}
                    >
                      {labelName}
                    </Checkbox>
                  </Box>
                );
              })}
            </FilterContentWrapper>
          </ScrollWrapper>

          <ShowResults hasFilters={filterApplied} onClick={handleCloseFilter} />
        </OutsideClickableWrapper>
      </DropdownOutlined.Items>
    </DropdownOutlined>
  );
};

const BrandFilter = connectRefinementList(BrandFilterComponent);

export default BrandFilter;
